import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { Button, Col, Row, Input, FormGroup, Label, Container, Badge, Alert, Card, CardBody } from "reactstrap";
import { FaClock, FaArrowRight, FaPlus, FaTrash, FaExclamationTriangle, FaCopy, FaArrowLeft } from "react-icons/fa";

const WhenComponent = ({ setSelectedIcon, updateCampaignData, campaignData }) => {
    // Önce DAY_OPTIONS'ı tanımla
    const DAY_OPTIONS = [
        { value: 'MONDAY', label: 'Monday' },
        { value: 'TUESDAY', label: 'Tuesday' },
        { value: 'WEDNESDAY', label: 'Wednesday' },
        { value: 'THURSDAY', label: 'Thursday' },
        { value: 'FRIDAY', label: 'Friday' },
        { value: 'SATURDAY', label: 'Saturday' },
        { value: 'SUNDAY', label: 'Sunday'},
    ];
    
    // Mevcut timeRanges varsa kullan, yoksa boş array başlat
    const [timeRanges, setTimeRanges] = useState(campaignData.timeRanges || []);
    
    // timeRanges'dan seçili günleri belirle
    const [selectedDays, setSelectedDays] = useState(() => {
        const days = timeRanges.map(range => ({
            value: range.day,
            label: DAY_OPTIONS.find(d => d.value === range.day)?.label || range.day
        }));
        return days;
    });
    
    const [timeErrors, setTimeErrors] = useState({});
    const [bulkStartTime, setBulkStartTime] = useState("08:00");
    const [bulkEndTime, setBulkEndTime] = useState("23:59");
    const [bulkTimeError, setBulkTimeError] = useState(false);
    
    // Bulk zaman değişikliklerini kontrol et
    useEffect(() => {
        if (bulkStartTime > bulkEndTime) {
            setBulkTimeError(true);
        } else {
            setBulkTimeError(false);
        }
    }, [bulkStartTime, bulkEndTime]);

    // timeRanges değiştiğinde campaignData'yı güncelle - ama sadece ilk render'dan sonra
    const isFirstRender = React.useRef(true);
    
    useEffect(() => {
        // İlk render'da güncelleme yapma
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        
        // Sonraki render'larda güncelle - timeRanges'in bir kopyasını geçelim
        const timeRangesCopy = JSON.parse(JSON.stringify(timeRanges));
        updateCampaignData({ timeRanges: timeRangesCopy });
    }, [timeRanges, updateCampaignData]);

    // Gün seçimi değiştiğinde timeRanges'i güncelle
    const handleDayChange = (selectedOptions) => {
        const selectedDayValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setSelectedDays(selectedOptions || []);
        
        // Mevcut zaman aralıklarını koru
        const existingTimeRanges = timeRanges.filter(range => 
            selectedDayValues.includes(range.day)
        );
        
        // Yeni günler için zaman aralıkları ekle
        const newDays = selectedDayValues.filter(day => 
            !timeRanges.some(range => range.day === day)
        );
        
        const newTimeRanges = [
            ...existingTimeRanges,
            ...newDays.map(day => ({ 
                day, 
                ranges: [{ startTime: bulkStartTime, endTime: bulkEndTime, id: Date.now() + Math.random() }]
            }))
        ];
        
        setTimeRanges(newTimeRanges);
        
        // Hata durumlarını temizle
        setTimeErrors({});
    };

    // Zaman aralığı ekle
    const addTimeRange = (dayIndex) => {
        const updatedTimeRanges = [...timeRanges];
        updatedTimeRanges[dayIndex].ranges.push({ 
            startTime: bulkStartTime, 
            endTime: bulkEndTime, 
            id: Date.now() + Math.random() 
        });
        setTimeRanges(updatedTimeRanges);
    };

    // Zaman aralığı kaldır
    const removeTimeRange = (dayIndex, rangeIndex) => {
        const updatedTimeRanges = [...timeRanges];
        
        // Eğer son zaman aralığı ise, günü tamamen kaldır
        if (updatedTimeRanges[dayIndex].ranges.length === 1) {
            // Günü kaldırmadan önce değerini al
            const dayToRemove = updatedTimeRanges[dayIndex].day;
            
            // Günü timeRanges'dan kaldır
            updatedTimeRanges.splice(dayIndex, 1);
            
            // Seçili günleri de güncelle
            setSelectedDays(prevSelectedDays => 
                prevSelectedDays.filter(day => day.value !== dayToRemove)
            );
        } else {
            // Değilse, sadece zaman aralığını kaldır
            updatedTimeRanges[dayIndex].ranges.splice(rangeIndex, 1);
        }
        
        setTimeRanges(updatedTimeRanges);
        
        // Hata durumunu temizle
        const errorKey = `${dayIndex}-${rangeIndex}`;
        const updatedErrors = { ...timeErrors };
        delete updatedErrors[errorKey];
        setTimeErrors(updatedErrors);
    };

    // Zaman değişikliği
    const handleTimeChange = (dayIndex, rangeIndex, field, value) => {
        const updatedTimeRanges = [...timeRanges];
        updatedTimeRanges[dayIndex].ranges[rangeIndex][field] = value;
        setTimeRanges(updatedTimeRanges);
        
        // Zaman aralığını kontrol et
        validateTimeRange(dayIndex, rangeIndex, updatedTimeRanges[dayIndex].ranges[rangeIndex]);
    };

    // Zaman aralığı doğrulama
    const validateTimeRange = (dayIndex, rangeIndex, range) => {
        const errorKey = `${dayIndex}-${rangeIndex}`;
        const updatedErrors = { ...timeErrors };
        
        if (range.startTime > range.endTime) {
            updatedErrors[errorKey] = "Start time cannot be after end time";
        } else if (range.endTime < range.startTime) {
            updatedErrors[errorKey] = "End time cannot be before start time";
        } else {
            delete updatedErrors[errorKey];
        }
        
        setTimeErrors(updatedErrors);
    };

    // Tüm günlere aynı zaman aralığını ekle
    const addBulkTimeRange = () => {
        if (bulkTimeError) return;
        
        const updatedTimeRanges = [...timeRanges];
        
        selectedDays.forEach(day => {
            const dayIndex = updatedTimeRanges.findIndex(range => range.day === day.value);
            
            if (dayIndex >= 0) {
                // Gün zaten varsa, yeni zaman aralığı ekle
                updatedTimeRanges[dayIndex].ranges.push({ 
                    startTime: bulkStartTime, 
                    endTime: bulkEndTime, 
                    id: Date.now() + Math.random() 
                });
            } else {
                // Gün yoksa, yeni gün ve zaman aralığı ekle
                updatedTimeRanges.push({ 
                    day: day.value, 
                    ranges: [{ 
                        startTime: bulkStartTime, 
                        endTime: bulkEndTime, 
                        id: Date.now() + Math.random() 
                    }]
                });
            }
        });
        
        setTimeRanges(updatedTimeRanges);
    };

    // Tüm günlerin zaman aralıklarını sıfırla ve yeni bir tane ekle
    const updateAllDaysWithSameTime = () => {
        if (bulkTimeError) return;
        
        const updatedTimeRanges = selectedDays.map(day => ({
            day: day.value,
            ranges: [{ 
                startTime: bulkStartTime, 
                endTime: bulkEndTime, 
                id: Date.now() + Math.random() 
            }]
        }));
        
        setTimeRanges(updatedTimeRanges);
        setTimeErrors({});
    };

    // Hata durumunu kontrol et
    const hasTimeErrors = Object.keys(timeErrors).length > 0;
    
    // Next butonunun durumunu kontrol et
    const isNextDisabled = timeRanges.length === 0 || hasTimeErrors;

    // Next butonuna tıklandığında
    const handleNext = () => {
        // Zaman aralıklarını güncelle
        updateCampaignData({ timeRanges });
        
        // Bir sonraki adıma geç
        setSelectedIcon(2);
    };

    // Select bileşenleri için özel stil
    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: '#fff',
            borderColor: '#ced4da'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#CC1977' : state.isFocused ? '#f8f9fa' : null,
            color: state.isSelected ? 'white' : 'black'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#CC1977'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: 'white',
            fontWeight: 500
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: 'white',
            ':hover': {
                backgroundColor: '#a01460',
                color: 'white'
            }
        })
    };

    return (
        <div className="when-component">
            <Container fluid>
                <Row className="mb-4">
                    <Col md={12}>
                        <div className="section-panel">
                            <div className="section-header">
                                <div className="step-number">
                                    <FaClock />
                                </div>
                                <h5>Schedule</h5>
                            </div>
                            <div className="section-body">
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label>Select Days</Label>
                                            <Select
                                                isMulti
                                                options={DAY_OPTIONS}
                                                value={selectedDays}
                                                onChange={handleDayChange}
                                                placeholder="Select days"
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                styles={customSelectStyles}
                                            />
                                        </FormGroup>
                                        
                                        {selectedDays.length > 0 && (
                                            <Card className="mt-3 mb-4 bulk-time-card">
                                                <CardBody>
                                                    <h6 className="mb-3">Manage All Selected Days</h6>
                                                    <Row className="align-items-center">
                                                        <Col md={4}>
                                                            <FormGroup>
                                                                <Label for="bulkStartTime" className="small">Start Time</Label>
                                                                <Input
                                                                    type="time"
                                                                    id="bulkStartTime"
                                                                    value={bulkStartTime}
                                                                    onChange={(e) => setBulkStartTime(e.target.value)}
                                                                    className={bulkTimeError ? 'is-invalid' : ''}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4}>
                                                            <FormGroup>
                                                                <Label for="bulkEndTime" className="small">End Time</Label>
                                                                <Input
                                                                    type="time"
                                                                    id="bulkEndTime"
                                                                    value={bulkEndTime}
                                                                    onChange={(e) => setBulkEndTime(e.target.value)}
                                                                    className={bulkTimeError ? 'is-invalid' : ''}
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                        <Col md={4} className="d-flex align-items-end">
                                                            <Button 
                                                                color="primary" 
                                                                size="sm" 
                                                                className="me-2 mt-2"
                                                                onClick={addBulkTimeRange}
                                                                disabled={bulkTimeError || selectedDays.length === 0}
                                                            >
                                                                <FaPlus size={12} className="me-1" /> Add to All
                                                            </Button>
                                                            <Button 
                                                                color="secondary" 
                                                                size="sm" 
                                                                className="mt-2"
                                                                onClick={updateAllDaysWithSameTime}
                                                                disabled={bulkTimeError || selectedDays.length === 0}
                                                            >
                                                                <FaCopy size={12} className="me-1" /> Reset All
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                    {bulkTimeError && (
                                                        <div className="text-danger small mt-2">
                                                            <FaExclamationTriangle size={12} className="me-1" />
                                                            Start time cannot be after end time
                                                        </div>
                                                    )}
                                                </CardBody>
                                            </Card>
                                        )}
                                        
                                        {timeRanges.length > 0 && (
                                            <div className="time-ranges">
                                                <Label>Time Ranges</Label>
                                                <div className="time-ranges-container">
                                                    {timeRanges.map((dayRange, dayIndex) => (
                                                        <div key={dayRange.day} className="day-container mb-3">
                                                            <div className="day-header">
                                                                <Badge color="light" className="day-badge">
                                                                    {DAY_OPTIONS.find(day => day.value === dayRange.day)?.label}
                                                                </Badge>
                                                                <Button 
                                                                    color="light" 
                                                                    size="sm" 
                                                                    className="add-time-btn"
                                                                    onClick={() => addTimeRange(dayIndex)}
                                                                >
                                                                    <FaPlus size={12} /> Add Time
                                                                </Button>
                                                            </div>
                                                            
                                                            {dayRange.ranges && dayRange.ranges.map((range, rangeIndex) => {
                                                                const errorKey = `${dayIndex}-${rangeIndex}`;
                                                                const hasError = timeErrors[errorKey];
                                                                
                                                                return (
                                                                    <div key={range.id} className={`time-range-row ${hasError ? 'has-error' : ''}`}>
                                                                        <div className="time-inputs">
                                                                            <Input
                                                                                type="time"
                                                                                value={range.startTime}
                                                                                onChange={(e) => handleTimeChange(dayIndex, rangeIndex, 'startTime', e.target.value)}
                                                                                className={`time-input ${hasError ? 'is-invalid' : ''}`}
                                                                            />
                                                                            <span className="time-separator">to</span>
                                                                            <Input
                                                                                type="time"
                                                                                value={range.endTime}
                                                                                onChange={(e) => handleTimeChange(dayIndex, rangeIndex, 'endTime', e.target.value)}
                                                                                className={`time-input ${hasError ? 'is-invalid' : ''}`}
                                                                            />
                                                                            <Button 
                                                                                color="light" 
                                                                                size="sm" 
                                                                                className="remove-time-btn"
                                                                                onClick={() => removeTimeRange(dayIndex, rangeIndex)}
                                                                            >
                                                                                <FaTrash size={12} />
                                                                            </Button>
                                                                        </div>
                                                                        {hasError && (
                                                                            <div className="time-error">
                                                                                <FaExclamationTriangle size={12} className="me-1" />
                                                                                {timeErrors[errorKey]}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {timeRanges.length === 0 && selectedDays.length > 0 && (
                                            <Alert color="info" className="mt-3">
                                                <FaExclamationTriangle className="me-2" />
                                                Please add time ranges for the selected days.
                                            </Alert>
                                        )}
                                        
                                        {selectedDays.length === 0 && (
                                            <Alert color="warning" className="mt-3">
                                                <FaExclamationTriangle className="me-2" />
                                                Please select at least one day to schedule your campaign.
                                            </Alert>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </Col>
                </Row>
                
                {/* Navigation Buttons */}
                <Row>
                    <Col md={12} className="d-flex justify-content-between">
                        <Button 
                            color="secondary" 
                            className="back-button" 
                            onClick={() => setSelectedIcon(0)}
                        >
                           <FaArrowLeft className="ms-0" />  Back: Who
                        </Button>
                        <Button 
                            color="primary" 
                            className="next-button" 
                            onClick={handleNext}
                            disabled={isNextDisabled}
                        >
                            Next: What <FaArrowRight className="ms-2" />
                        </Button>
                    </Col>
                </Row>
            </Container>
            
            <style jsx="true">{`
                .when-component {
                    padding: 15px 0;
                    width: 100%;
                }
                
                .section-panel {
                    background: white;
                    border: 1px solid #dee2e6;
                    border-radius: 8px;
                    height: 100%;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                
                .section-header {
                    display: flex;
                    align-items: center;
                    padding: 10px 15px;
                    background-color: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    border-radius: 8px 8px 0 0;
                }
                
                .step-number {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background-color: #28a745;
                    color: white;
                    margin-right: 10px;
                    font-size: 14px;
                }
                
                .section-header h5 {
                    margin: 0;
                    font-weight: 600;
                    font-size: 16px;
                    color: #343a40;
                }
                
                .section-body {
                    padding: 20px 15px;
                }
                
                .bulk-time-card {
                    background-color: #f8f9fa;
                    border-color: #e9ecef;
                }
                
                .time-ranges {
                    margin-top: 20px;
                }
                
                .time-ranges-container {
                    margin-top: 10px;
                }
                
                .day-container {
                    background-color: #f8f9fa;
                    border-radius: 8px;
                    padding: 15px;
                    border: 1px solid #e9ecef;
                }
                
                .day-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }
                
                .day-badge {
                    font-size: 14px;
                    padding: 8px 12px;
                    background-color: #e9ecef;
                    color: #495057;
                }
                
                .add-time-btn {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    padding: 4px 8px;
                    font-size: 12px;
                }
                
                .time-range-row {
                    margin-bottom: 10px;
                    padding: 10px;
                    background-color: white;
                    border-radius: 5px;
                    border: 1px solid #e9ecef;
                }
                
                .time-range-row.has-error {
                    border-color: #dc3545;
                    background-color: #fff8f8;
                }
                
                .time-inputs {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                
                .time-input {
                    flex: 1;
                }
                
                .time-separator {
                    color: #6c757d;
                    font-weight: 500;
                }
                
                .remove-time-btn {
                    padding: 4px 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .time-error {
                    margin-top: 5px;
                    color: #dc3545;
                    font-size: 12px;
                    display: flex;
                    align-items: center;
                }
                
                .back-button, .next-button {
                    display: flex;
                    align-items: center;
                    padding: 6px 16px;
                    font-weight: 500;
                    font-size: 15px;
                }
                
                .back-button {
                    background-color: #6c757d;
                    border-color: #6c757d;
                }
                
                .next-button {
                    background-color: #CC1977;
                    border-color: #CC1977;
                }
                
                .next-button:hover:not(:disabled) {
                    background-color: #a91462;
                    border-color: #a91462;
                }
                
                .next-button:disabled {
                    background-color: #e9ecef;
                    border-color: #e9ecef;
                    color: #6c757d;
                }
            `}</style>
        </div>
    );
};

export default WhenComponent;